/** Apple-like motion presets (spring physics, subtle scale + fade). */

export const APPLE_SPRING = {
  type: 'spring' as const,
  stiffness: 380,
  damping: 34,
  mass: 0.88,
};

export const APPLE_SPRING_GENTLE = {
  type: 'spring' as const,
  stiffness: 320,
  damping: 36,
  mass: 0.95,
};

/** AI side pane push layout (draft editor — width + sibling reflow). */
export const AI_PANE_SPRING = {
  type: 'spring' as const,
  stiffness: 260,
  damping: 34,
  mass: 0.92,
};

/** Route cross-fade — opacity only (no y shift) to avoid layout gaps during popLayout overlap. */
export const PAGE_TRANSITION = {
  variants: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  transition: {
    duration: 0.18,
    ease: [0.22, 0.08, 0.2, 1] as [number, number, number, number],
  },
};

/** Skeleton → loaded content (Cases, Drafts, Dashboard lists). */
export const CONTENT_REVEAL = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: {
    duration: 0.32,
    ease: [0.22, 0.08, 0.2, 1] as [number, number, number, number],
  },
};

/** After async skeleton — short fade so content is not delayed further. */
export const CONTENT_REVEAL_FAST = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  transition: {
    duration: 0.18,
    ease: [0.22, 0.08, 0.2, 1] as [number, number, number, number],
  },
};

export const staggerContainerVariants = {
  hidden: { opacity: 1 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.085, delayChildren: 0.04 },
  },
};

/** Home cards: move + scale only (opacity stays 1 to avoid flash after route fade). */
export const staggerItemVariants = {
  hidden: { opacity: 1, y: 18, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: APPLE_SPRING,
  },
};

export function revealDelay(index: number, step = 0.06, base = 0.03) {
  return base + index * step;
}

/** Case file main/sub tab panels — lighter than page mount. */
export const TAB_PANEL_REVEAL = {
  initial: { opacity: 0, y: 12, scale: 0.99 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -6, scale: 0.99 },
  transition: {
    type: 'spring' as const,
    stiffness: 400,
    damping: 32,
    mass: 0.85,
  },
};

/** Modal overlay fade — Apple ease curve. */
export const MODAL_OVERLAY_FADE = {
  duration: 0.22,
  ease: [0.22, 0.08, 0.2, 1] as [number, number, number, number],
};

/** Modal panel enter/exit — spring scale + lift. */
export const MODAL_PANEL_ENTER = {
  initial: { opacity: 0, scale: 0.96, y: 16 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.96, y: 12 },
  transition: {
    type: 'spring' as const,
    stiffness: 380,
    damping: 34,
    mass: 0.88,
  },
  exitTransition: {
    duration: 0.2,
    ease: [0.22, 0.08, 0.2, 1] as [number, number, number, number],
  },
};

/** Layout resize spring for modal shells (height/width changes). */
export const MODAL_LAYOUT_SPRING = {
  type: 'spring' as const,
  stiffness: 220,
  damping: 30,
  mass: 1.05,
};

/** Explicit height/width tween — slower and smoother than layout defaults. */
export const MODAL_SIZE_TRANSITION = {
  type: 'spring' as const,
  stiffness: 200,
  damping: 28,
  mass: 1.1,
};

export type StepDirection = 'forward' | 'back';

/** Directional step panel variants for multi-step modals. */
export function modalStepVariants(direction: StepDirection) {
  const enterX = direction === 'forward' ? 10 : -10;
  const exitX = direction === 'forward' ? -6 : 6;
  return {
    initial: { opacity: 0, x: enterX },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: exitX },
    transition: {
      type: 'spring' as const,
      stiffness: 340,
      damping: 34,
      mass: 0.9,
    },
  };
}
